import { Component, inject } from '@angular/core';
import { BaseViewComponent } from '../base-view.component';
import { StateChangedModel } from '../../common/services/state.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { SecureDataService } from '../../common/services/securedata.sevice';
import { take } from 'rxjs';
import { LanguageStateService } from '../../common/services/language-state.service';
import { isSignatureRequired } from '../../common/utils/signature-type';

export interface TermsConditionModel {
  id: string,
  consentLabel: string,
  consentText: string,
  consentHtml: string,
  termText: string,
  termHtml: string,
  termUrl: string,
  seq: number
}

export interface TermsConditionRequest {
  id: string,
  isChecked: boolean;
}

export interface TermsConditionEventRequest {
  data: TermsConditionRequest[];
  isSign: boolean;
}

enum SignatureType {
  none,
  full,
  initials
}

@Component({
    selector: 'app-terms-conditions',
    templateUrl: './terms-conditions.component.html',
    styleUrl: './terms-conditions.component.scss',
    standalone: false
})
export class TermsConditionsComponent extends BaseViewComponent {
  showCancelDialog: boolean;
  signType = SignatureType.none;
  languageService: LanguageStateService = inject(LanguageStateService)
  protected override onSecureDataChanged(val: StateChangedModel): void {

    if (val && val.Data) {
      this.termsModel = val.Data;
      this.allTerms = (this.termsModel.terms as TermsConditionModel[])?.sort((a, b) => a.seq - b.seq) ?? [];
      this.signType = this.termsModel.sigType;
      this.signatureRequired = isSignatureRequired(this.termsModel.sigType);

      this.allTerms.forEach(term => {
        this.isConsumerAgreementChecked.push({ id: term.id, isChecked: false });
        this.termsAndConditionsForm.addControl(
          `term_${term.id}`,
          new FormControl(false, Validators.requiredTrue)
        )

      });

      if (this.signatureRequired) {
        this.termsAndConditionsForm.addControl(
          `signType`,
          new FormControl(null, Validators.required)
        )
      }

    }

  }

  #secureDataService: SecureDataService = inject(SecureDataService)


  sanitizer = inject(DomSanitizer);
  termsAndConditionsForm: FormGroup = new FormGroup({});
  popupHeader = 'Terms and Conditions';
  popupTitle = 'I have read and agree to the above.';
  termsText: any;

  signatureRequired = false;
  isConsumerAgreementChecked: TermsConditionRequest[] = [];
  isConsumerAgreementVisible = false;

  isPaperLessBillingChecked = false;
  isPaperLessBillingVisible = false;

  isPrescreenOptoutChecked = false;
  isPrescreenOptoutVisible = false;
  isRequiredConfirmVisible = false;

  externalLinkPopup = false;
  termsModel: any;
  allTerms: TermsConditionModel[] = [];
  sanitizedExternalLink: string = '';
  showErrors: boolean[] = [false];
  selectedItem = '';
  name = '';
  nameSelected = false;
  isSubmitted = false;




  makeLinksClickable(text: string): SafeHtml {
    if (!text) return ''

    // Regular expression to match URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g

    // Replace URLs with anchor tags
    const textWithLinks = text.replace(urlRegex, url => {
      this.sanitizedExternalLink = url;
      return `<a href="#" target="_self" class="text-primary hover:text-primary-700 underline">${url}</a>`;
    }

    )
    return this.sanitizer.bypassSecurityTrustHtml(textWithLinks)
  }

  getSafeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  onConsumerAgreementChecked(termId: string, event: any) {
    if (event.checked) {
      const term = this.allTerms.find(t => t.id === termId);
      const hasUrl = term?.termUrl && term.termUrl.trim() !== '';

      // Only force link-click validation if this term has a URL
      if (hasUrl) {
        const item = this.isConsumerAgreementChecked.find(t => t.id === termId);
        if (item !== undefined) {
          item.isChecked = false;
        }
        this.notificationService.showWarning('Please read the terms by clicking the link before accepting.');
        const control = this.termsAndConditionsForm.get(`term_${termId}`);
        if (control) {
          control.setValue(false);
        }
      }
      // If no URL (inline HTML only), allow checkbox freely — no reset needed
    }
  }

  onExternalPopupLinkClick(checked: boolean) {
    if (!checked) {
      this.externalLinkPopup = checked;
      this.isConsumerAgreementVisible = !checked;
    } else {
      this.externalLinkPopup = false;
      window.open(this.sanitizedExternalLink, '_blank');
    }
  }

  handleLinkClick(event: Event) {
    const target = event.target as HTMLElement;

    if (target.tagName === 'A') {
      event.preventDefault(); // Prevent default navigation
      this.externalLinkPopup = true;
      this.isConsumerAgreementVisible = false;
    }
  }

  onConsumerAgreementClick(termText: any, termUrl: string, id: string) {
    this.selectedItem = id;

    const hasUrl = termUrl && termUrl.trim() !== '';
    const hasText = termText && termText.trim() !== '';

    if (hasUrl) {
      window.open(termUrl, '_blank');
      setTimeout(() => {
        const control = this.termsAndConditionsForm.get(`term_${id}`);
        if (control) {
          control.setValue(true);
          control.markAsTouched();
        }
      }, 0);
    } else if (hasText) {
      this.termsText = this.makeLinksClickable(termText);
      this.isConsumerAgreementVisible = true;
      this.externalLinkPopup = false;
    }

    return false;
  }

  private signedRequiredValidation() {
    if (this.name.length == 0) {
      this.isRequiredConfirmVisible = true;
      this.signType = SignatureType.none;
      this.nameSelected = true;
      return;
    }
    else {
      this.signType = SignatureType.initials;
      this.nameSelected = false;

    }
  }

  onFormSubmit() {

    if (this.termsAndConditionsForm.valid) {

      const requestData: TermsConditionRequest[] = [];
      this.isConsumerAgreementChecked.forEach((term) => {
        requestData.push({ id: term.id, isChecked: true });
      });

      const termConditonEventRequest = {
        signature: this.termsAndConditionsForm.get('signType')?.value,
        timestamp: new Date().toISOString(),
        data: requestData,
        isSign: !this.signatureRequired ? false : true
      }
      this.#secureDataService.submitCustomerData(termConditonEventRequest, 'TermsAndConditionInfo')
        .subscribe({
          next: () => {
            this.navigateWaiting();
          },
          error: (err) => {
            this.notificationService.showError(err);
          }
        });

    } else {
      this.isRequiredConfirmVisible = true;
    }

  }

  onFormCancel() {
    this.showCancelDialog = true;

  }


  onCancelConfimation(data: unknown) {
    this.showCancelDialog = false;

    if (data && (data as { data: { success: boolean } }).data?.success) {

      this.isSubmitted = false;
      this.nameSelected = false;
      this.signType = SignatureType.none;
      this.name = '';
      this.termsAndConditionsForm.reset();

      this.#secureDataService.cancel('TermsAndConditionInfo').pipe(take(1)).subscribe(
        {
          next: () => {
            this.navigateWaiting(true);
          },
          error: (err) => this.onCancelError('TermsAndConditionInfo', err)
        })
    }

  }

  onNameChange() {
    this.nameSelected = false;
  }

  onPopupSubmit() {
    this.isConsumerAgreementVisible = false;
    this.externalLinkPopup = false;
    this.isConsumerAgreementChecked[this.selectedItem] = true;
    const control = this.termsAndConditionsForm.get(`term_${this.selectedItem}`)
    if (control) {
      control.setValue(true)
    }
  }

  toggleNameRequirement() {
    this.nameSelected = !this.nameSelected;
  }

  onPopupCancel() {
    this.isConsumerAgreementVisible = false;
    this.externalLinkPopup = false;
    this.isConsumerAgreementChecked[this.selectedItem] = false;
  }



  onRequiredFieldSubmit() {
    this.termsAndConditionsForm.markAllAsTouched();

    if (this.name.length === 0) {
      this.nameSelected = true;
      this.signType = SignatureType.none;
    }
    else {
      this.nameSelected = false;
      this.signType = SignatureType.initials
    }
    for (let i = 0; i < this.isConsumerAgreementChecked.length; i++) {
      if (!this.isConsumerAgreementChecked[i]) {
        this.showErrors[i] = true;
      }

    }
    this.isRequiredConfirmVisible = false;
  }


  formRequiredValidationShow() {

    if (this.isSubmitted) {

      return !this.signatureRequired ?
        !this.termsAndConditionsForm.valid : !(this.name.length > 0 && this.termsAndConditionsForm.valid);
    }


    return false;


  }
}


